import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '@mui/material';
import { EditInstallerForm } from 'components/Drawer/types';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useInstallers from 'hooks/useInstallers/useInstallers';
import useModal from 'hooks/useModal/useModal';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EditInstallerDrawer, MobileType } from 'state/types';
import { lightMuiTheme } from 'theme/theme';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import EditInstaller from 'ui/organisms/Drawer/variants/EditInstaller';

const EditInstallerVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { register, handleSubmit, setValue } = useForm<EditInstallerForm>();
  const { drawerState, hideDrawer } = useDrawer<EditInstallerDrawer>();
  const { showModal } = useModal();
  const [nameInputValue, setNameInputValue] = useState<string>();
  const [emailInputValue, setEmailInputValue] = useState<string>();
  const [mobileDeviceStatusAccess, setMobileDeviceStatusAccess] = useState<boolean>();
  const [mobileDeviceStatusConfig, setMobileDeviceStatusConfig] = useState<boolean>();
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { updateInstallerMember, updateInstallerMemberLoading } = useInstallers({
    handleFetchError
  });

  const onSubmit = async () => {
    setShowValidation(true);
    if (!updateInstallerMemberLoading && emailInputValue && nameInputValue && nameInputValidation === undefined) {
      updateInstallerMember(emailInputValue, nameInputValue);
      hideDrawer();
    }
  };

  const handleDeleteInstaller = () => {
    showModal({ type: 'deleteInstaller' });
  };

  const handleOpenResendInvitationModal = (type: MobileType) => {
    showModal({
      type: 'resendInvitation',
      contentValue: {
        visitorId: drawerState.contentValue.id,
        email: emailInputValue!,
        mobileDevices: drawerState.contentValue.mobileDevices,
        type
      }
    });
  };

  useEffect(() => {
    if (drawerState.contentValue) {
      if (nameInputValue === undefined) setNameInputValue(drawerState.contentValue.name);
      if (emailInputValue === undefined) setEmailInputValue(drawerState.contentValue.email || undefined);
      if (mobileDeviceStatusConfig === undefined)
        setMobileDeviceStatusConfig(
          drawerState.contentValue.mobileDevices.find((device) => device.role === 'CONFIG')?.isRegistered
        );
      if (mobileDeviceStatusAccess === undefined)
        setMobileDeviceStatusAccess(
          drawerState.contentValue.mobileDevices.find((device) => device.role === 'ACCESS')?.isRegistered
        );
    }
  }, [drawerState, emailInputValue, mobileDeviceStatusAccess, mobileDeviceStatusConfig, nameInputValue]);

  useEffect(() => {
    if (nameInputValue === '') setNameInputValidation(translation.name_cannot_be_empty);
    else setNameInputValidation(undefined);
  }, [nameInputValue, translation.name_cannot_be_empty]);

  useEffect(() => {
    setValue('name', nameInputValue!);
    setValue('email', emailInputValue!);
  }, [emailInputValue, nameInputValue, setValue]);
  return (
    <DrawerTemplate
      title={translation.edit_installer}
      headerSideText={
        <IconButton size="small" style={{ color: lightMuiTheme.palette.error.main }} id="drawer-delete-installer">
          <DeleteOutlineIcon fontSize="medium" data-testid="svg-delete" style={{ marginLeft: 0 }} />
        </IconButton>
      }
      headerSideTextOnClick={handleDeleteInstaller}
      confirmButtonText={translation.save}
      closeButtonText={translation.cancel}
      closeButton
      id="drawer-edit-installer"
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={
        !drawerState.contentValue.isEditable || (showValidation && nameInputValidation !== undefined)
      }
    >
      <EditInstaller
        register={register}
        nameInputValue={nameInputValue!}
        handleNameInputOnChange={setNameInputValue}
        nameInputValidation={nameInputValidation}
        emailInputValue={emailInputValue!}
        statusAccess={mobileDeviceStatusAccess!}
        statusConfig={mobileDeviceStatusConfig!}
        showValidation={showValidation}
        isEditable={drawerState.contentValue.isEditable}
        isResendInvitationButtonVisibleConfig={!mobileDeviceStatusConfig!}
        isResendInvitationButtonVisibleAccess={!mobileDeviceStatusAccess!}
        handleOnResendInvitationClick={handleOpenResendInvitationModal}
      />
    </DrawerTemplate>
  );
};
export default EditInstallerVariant;
